// Calendar source/department display-tag + filter helpers
// (feat-calendar-source-department-filters-sep1).
//
// Pure, framework-free helpers so the filter/badge logic is unit-testable apart
// from the React page. The backend already tags native rows via
// appointment_tags.py; these helpers keep the FRONTEND resilient by deriving the
// same two tags from whatever fields a loaded row carries (B.PRO view rows or
// native /api/appointments rows), and provide the client-side filter + counts.

export const SOURCE_AIVOICE = 'aivoice';
export const SOURCE_BPRO = 'bpro';
export const DEPT_VENTAS = 'ventas';
export const DEPT_POSTVENTA = 'postventa';

// Sucursal (branch) switching (feat-calendar-sucursal-switch-sep2). Grupo Cruces
// runs 3 GM sucursales, each its own B.PRO branch db. Rows without a resolvable
// branch bucket into SUCURSAL_UNKNOWN so they are never dropped silently.
export const SUCURSAL_ALL = '__all__';
export const SUCURSAL_UNKNOWN = 'Sin sucursal';

// Known branch db -> es-MX label (mirror of backend bpro_sucursales._BRANCH_LABELS).
// Used as a display fallback AND as the static option list when the live
// /api/bpro/sucursales read is unavailable (testing ECS at 0 / B.PRO down), so
// the sucursal filter chips still render (with counts 0) instead of vanishing.
//
// P1 calendar tabs (Roger 12:39 CST, 2026-10-21) labelled GCGMVILLA rows
// "Villahermosa: Deportiva" (display override of the backend's plain
// "Villahermosa" label, which the agent still speaks / B.PRO still uses) and
// added a synthetic "Villahermosa: Tabasco 2000" chip bucketed by the row's
// `requested_site`. Jocelin 12:48 CST, 2026-10-22 (via Roger): "Solo hay taller
// en Deportiva. Tabasco 2000 solo es punto de venta por ahora" -> Tabasco 2000 is
// a SALES site with no taller, so there is no Tabasco 2000 service bucket: the
// chip is gone and any row that still carries `requested_site='Tabasco 2000'`
// (the customer SAID Tabasco 2000; the cita is in Deportiva's db) folds into
// "Villahermosa: Deportiva" by its branch db. The label mapping is kept.
const BRANCH_DB_LABELS = {
  GCGMVILLA: 'Villahermosa: Deportiva',
  GCGMCARDENAS: 'Cárdenas',
  GCGMCOMALCALCO: 'Comalcalco',
};

// The branch db a row's `requested_site` folds into (Jocelin oct22): a cita the
// customer requested "for Tabasco 2000" is a Deportiva cita. Used only when the
// row carries NO branch db / location_id / branch of its own, so such a row is
// never dropped into "Sin sucursal".
const REQUESTED_SITE_FOLDS_INTO = {
  'tabasco 2000': 'GCGMVILLA',
};

// Resolve a row's `requested_site` to the branch db it folds into (null when the
// row has no / an unknown requested_site).
export function requestedSiteBranch(appt) {
  const site = norm(appt && appt.requested_site);
  if (!site) return null;
  return REQUESTED_SITE_FOLDS_INTO[site] || null;
}

// Static fallback option list (Grupo Cruces' 3 GM sucursales) in display order.
// deriveSucursalOptions() below prefers the live API list and only falls back to
// this when the API returned nothing, so the chips are never hidden just because
// B.PRO is down or the month has 0 rows.
export const FALLBACK_SUCURSAL_OPTIONS = Object.freeze([
  { value: 'GCGMVILLA', label: 'Villahermosa: Deportiva' },
  { value: 'GCGMCARDENAS', label: 'Cárdenas' },
  { value: 'GCGMCOMALCALCO', label: 'Comalcalco' },
]);

// Resolve the sucursal FETCH option list (one /api/bpro/calendar read per entry).
// Precedence:
//   1. the live API list (data-driven, per-tenant) when it has entries;
//   2. otherwise the static FALLBACK list, so chips render even with 0 rows /
//      B.PRO unavailable (counts then simply read 0).
// Any sucursal keys actually present in the loaded rows but missing from the
// chosen list are appended, so a row's sucursal is never un-filterable.
// Known GM branch dbs always use the display label above ("Villahermosa:
// Deportiva") even when the API still says "Villahermosa".
export function deriveSucursalOptions(apiOptions, appts) {
  const base = (Array.isArray(apiOptions) && apiOptions.length)
    ? apiOptions.map((o) => ({ value: o.value, label: BRANCH_DB_LABELS[o.value] || o.label }))
    : FALLBACK_SUCURSAL_OPTIONS.map((o) => ({ ...o }));
  const seen = new Set(base.map((o) => o.value));
  (appts || []).forEach((a) => {
    const key = deriveSucursalKey(a);
    if (key && key !== SUCURSAL_UNKNOWN && !seen.has(key)) {
      seen.add(key);
      base.push({ value: key, label: branchLabel(key) });
    }
  });
  return base;
}

// Resolve the sucursal CHIP option list (what the filter row renders). Jocelin
// oct22: exactly the fetch options -- "Villahermosa: Deportiva", "Cárdenas",
// "Comalcalco" (+ any custom db seen in rows). No synthetic site chip: Tabasco
// 2000 has no taller, its requested rows fold into Villahermosa: Deportiva.
export function deriveSucursalChipOptions(apiOptions, appts) {
  return deriveSucursalOptions(apiOptions, appts);
}

const POSTVENTA_ALIASES = new Set([
  'postventa', 'posventa', 'post-venta', 'post_venta',
  'service', 'servicio', 'taller', 'aftersales',
]);
const AIVOICE_CHANNELS = new Set(['voice', 'whatsapp', 'web', 'ui']);

const norm = (v) => String(v == null ? '' : v).trim().toLowerCase();

export function normalizeDepartment(dept) {
  let d = norm(dept);
  if (!d) return DEPT_VENTAS;
  if (d.includes('#')) d = d.split('#').pop();
  return POSTVENTA_ALIASES.has(d) || d === DEPT_POSTVENTA ? DEPT_POSTVENTA : DEPT_VENTAS;
}

// Derive the display source for one loaded appointment row.
// Precedence: explicit source=bpro -> external cita id -> explicit source=aivoice
// -> aivoice origin channel -> default aivoice.
export function deriveSource(appt) {
  const s = norm(appt && appt.source);
  if (s === SOURCE_BPRO) return SOURCE_BPRO;
  // A real external DMS cita id (bpro_cita_id) is decisive B.PRO evidence.
  if (norm(appt && appt.bpro_cita_id)) return SOURCE_BPRO;
  // An explicit aivoice source / origin channel wins over the ambiguous
  // external_cita_id (native rows overload external_cita_id with their own id).
  if (s === SOURCE_AIVOICE || AIVOICE_CHANNELS.has(s)) return SOURCE_AIVOICE;
  // A B.PRO view row (no source, but a CIT_IDCITA) -> bpro.
  if (norm(appt && appt.external_cita_id)) return SOURCE_BPRO;
  return SOURCE_AIVOICE;
}

export function deriveDepartment(appt) {
  const d = norm(appt && appt.department);
  if (d) return normalizeDepartment(d);
  const pk = norm((appt && (appt.pk || appt.partition || appt.partition_key)) || '');
  if (pk.includes('#postventa')) return DEPT_POSTVENTA;
  return DEPT_VENTAS;
}

// Human sucursal label for a branch db value (falls back to the raw value, or
// SUCURSAL_UNKNOWN when empty).
export function branchLabel(branchDb) {
  const v = String(branchDb == null ? '' : branchDb).trim();
  if (!v) return SUCURSAL_UNKNOWN;
  return BRANCH_DB_LABELS[v] || v;
}

// Derive the BRANCH db key for a loaded appointment row (ignores requested_site;
// see deriveSucursalKey for the fold). The backend already
// tags B.PRO rows with branch_db + sucursal; native aivoice rows may carry a
// `branch`/`location` free-text field. Precedence:
//   explicit branch_db -> branch (if it matches a known db) -> SUCURSAL_UNKNOWN.
// The KEY is what we filter/group on; the LABEL is for display.
export function deriveBranchKey(appt) {
  // Precedence: explicit branch_db -> native location_id (canonical branch DB
  // written by the WA/native booking path, #1286/#1360) -> free-text branch.
  // Reading location_id here is what stops native aivoice bookings — which store
  // the canonical branch DB under location_id, NOT branch_db — from bucketing into
  // "Sin sucursal" and losing their sucursal chip on the calendar.
  const db = norm(appt && appt.branch_db);
  if (db) {
    // normalize to the canonical db casing when it's a known branch
    const known = Object.keys(BRANCH_DB_LABELS).find((k) => k.toLowerCase() === db);
    return known || (appt.branch_db ? String(appt.branch_db).trim() : SUCURSAL_UNKNOWN);
  }
  const loc = norm(appt && appt.location_id);
  if (loc) {
    const known = Object.keys(BRANCH_DB_LABELS).find((k) => k.toLowerCase() === loc);
    return known || (appt.location_id ? String(appt.location_id).trim() : SUCURSAL_UNKNOWN);
  }
  const branch = String((appt && appt.branch) || '').trim();
  if (branch) {
    const known = Object.keys(BRANCH_DB_LABELS).find((k) => k.toLowerCase() === branch.toLowerCase());
    return known || branch;
  }
  return SUCURSAL_UNKNOWN;
}

// Derive a stable sucursal KEY for a loaded appointment row: its branch db
// (Jocelin oct22: a `requested_site` such as Tabasco 2000 never makes a bucket
// of its own -- the cita lives in the taller's db and is grouped there); a row
// with no branch of its own but a known requested_site folds into that site's
// taller db instead of "Sin sucursal".
export function deriveSucursalKey(appt) {
  const key = deriveBranchKey(appt);
  if (key && key !== SUCURSAL_UNKNOWN) return key;
  return requestedSiteBranch(appt) || key;
}

// ---------------------------------------------------------------------------
// Per-sucursal visual accent (feat-calendar-sucursal-filter-distinguish-sep4).
// Distinguish citas at a glance by sucursal WITHOUT relying on color alone
// (color-blind safe: every accent pairs a hue with a short initial "V/C/Co" so
// the badge is legible in grayscale). Keeps the existing SOURCE colors (B.PRO
// blue / IA amber) untouched — sucursal uses a SEPARATE hue family (emerald/
// fuchsia/sky) so the two dimensions never collide.
const SUCURSAL_ACCENT = {
  GCGMVILLA: {
    initial: 'V',
    dot: 'bg-emerald-500',
    border: 'border-l-emerald-500',
    badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200',
  },
  GCGMCARDENAS: {
    initial: 'C',
    dot: 'bg-fuchsia-500',
    border: 'border-l-fuchsia-500',
    badge: 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/50 dark:text-fuchsia-200',
  },
  GCGMCOMALCALCO: {
    initial: 'Co',
    dot: 'bg-sky-500',
    border: 'border-l-sky-500',
    badge: 'bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-200',
  },
};

// Neutral accent for the "Sin sucursal" bucket / any unknown branch db, so a row
// without a resolvable sucursal still renders a legible (gray) chip rather than
// nothing. `initial` falls back to the first letters of a custom db label.
const SUCURSAL_ACCENT_UNKNOWN = {
  initial: '?',
  dot: 'bg-gray-400',
  border: 'border-l-gray-300 dark:border-l-gray-600',
  badge: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

// Return the accent descriptor for a sucursal KEY (as produced by
// deriveSucursalKey). Known GM branches get their fixed accent; a custom/unknown
// branch db gets the neutral accent with its first letters as the initial.
export function sucursalAccent(sucursalKey) {
  if (sucursalKey && SUCURSAL_ACCENT[sucursalKey]) return SUCURSAL_ACCENT[sucursalKey];
  if (sucursalKey && sucursalKey !== SUCURSAL_UNKNOWN) {
    const stem = String(sucursalKey).replace(/^GCGM/i, '').slice(0, 2) || '?';
    const initial = stem.charAt(0).toUpperCase() + stem.slice(1).toLowerCase();
    return { ...SUCURSAL_ACCENT_UNKNOWN, initial };
  }
  return SUCURSAL_ACCENT_UNKNOWN;
}

// Short initial ("V"/"C"/"Co") used on dots/rows where a full label is too wide.
export function sucursalInitial(sucursalKey) {
  return sucursalAccent(sucursalKey).initial;
}

// Return a NEW row with derived _source/_department/_sucursal display tags.
export function tagAppointment(appt) {
  const sucKey = deriveSucursalKey(appt);
  return {
    ...appt,
    _source: deriveSource(appt),
    _department: deriveDepartment(appt),
    _sucursal: sucKey,
    _sucursalLabel: sucKey === SUCURSAL_UNKNOWN ? SUCURSAL_UNKNOWN : branchLabel(sucKey),
  };
}

// Filter a tagged list to one sucursal key (SUCURSAL_ALL = no filtering).
export function filterBySucursal(appts, sucursalKey) {
  if (!sucursalKey || sucursalKey === SUCURSAL_ALL) return appts || [];
  return (appts || []).filter(
    (a) => (a._sucursal || deriveSucursalKey(a)) === sucursalKey
  );
}

// ---------------------------------------------------------------------------
// Multi-select sucursal filter chips (feat-calendar-filters-sep5). Unlike the
// old single-select switcher, the chips let the operator show several sucursales
// at once. State is a plain object { <sucursalKey>: boolean }; a key that is
// absent is treated as ON (default show-all), so newly-appearing sucursales are
// never hidden until explicitly toggled off. SUCURSAL_UNKNOWN rows are always
// shown (they have no chip to switch them off) so nothing is silently dropped.
export const SUCURSAL_FILTERS_STORAGE_KEY = 'calendar.sucursalFilters.v1';

// Build the default (all-ON) chip state for a set of option values.
export function defaultSucursalFilters(optionValues) {
  const out = {};
  (optionValues || []).forEach((v) => { if (v) out[v] = true; });
  return out;
}

// A sucursal chip is ON unless its key is explicitly set to false. Unknown keys
// (SUCURSAL_UNKNOWN or a key with no chip) are always visible.
export function sucursalChipOn(filters, key) {
  if (!key || key === SUCURSAL_UNKNOWN) return true;
  const f = (filters && typeof filters === 'object') ? filters : {};
  return f[key] !== false;
}

// Filter a tagged list by the multi-select chip state. Rows whose sucursal chip
// is OFF are hidden; rows without a resolvable sucursal always pass.
export function filterBySucursalChips(appts, filters) {
  return (appts || []).filter((a) => sucursalChipOn(filters, a._sucursal || deriveSucursalKey(a)));
}

export function loadSucursalFilters(storage) {
  try {
    const store = storage || (typeof window !== 'undefined' ? window.localStorage : null);
    if (!store) return {};
    const raw = store.getItem(SUCURSAL_FILTERS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === 'object') {
      const out = {};
      Object.keys(parsed).forEach((k) => { if (typeof parsed[k] === 'boolean') out[k] = parsed[k]; });
      return out;
    }
    return {};
  } catch (_e) {
    return {};
  }
}

export function saveSucursalFilters(filters, storage) {
  try {
    const store = storage || (typeof window !== 'undefined' ? window.localStorage : null);
    if (!store) return;
    const out = {};
    if (filters && typeof filters === 'object') {
      Object.keys(filters).forEach((k) => { if (typeof filters[k] === 'boolean') out[k] = filters[k]; });
    }
    store.setItem(SUCURSAL_FILTERS_STORAGE_KEY, JSON.stringify(out));
  } catch (_e) {
    /* best-effort: persistence must never break the calendar */
  }
}

// Count appointments per sucursal key over a tagged list. Returns a plain object
// { <key>: n, ... } including SUCURSAL_UNKNOWN when present.
export function computeSucursalCounts(appts) {
  const counts = {};
  (appts || []).forEach((a) => {
    const key = a._sucursal || deriveSucursalKey(a);
    counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
}

// Persisted sucursal selection (same pattern as filters, #1277).
export const SUCURSAL_STORAGE_KEY = 'calendar.sucursal.v1';

export function loadSucursal(storage) {
  try {
    const store = storage || (typeof window !== 'undefined' ? window.localStorage : null);
    if (!store) return SUCURSAL_ALL;
    const raw = store.getItem(SUCURSAL_STORAGE_KEY);
    return raw && String(raw).trim() ? String(raw) : SUCURSAL_ALL;
  } catch (_e) {
    return SUCURSAL_ALL;
  }
}

export function saveSucursal(sucursalKey, storage) {
  try {
    const store = storage || (typeof window !== 'undefined' ? window.localStorage : null);
    if (!store) return;
    store.setItem(SUCURSAL_STORAGE_KEY, String(sucursalKey || SUCURSAL_ALL));
  } catch (_e) {
    /* best-effort: persistence must never break the calendar */
  }
}

// Default filter state: everything ON (show all), so nothing is hidden until the
// user opts to reduce clutter.
export const DEFAULT_FILTERS = Object.freeze({
  [SOURCE_BPRO]: true,
  [SOURCE_AIVOICE]: true,
  [DEPT_VENTAS]: true,
  [DEPT_POSTVENTA]: true,
});

export const FILTERS_STORAGE_KEY = 'calendar.filters.v1';

// Merge a persisted (possibly partial / stale) filter object onto the defaults
// so a missing/legacy key never hides appointments unexpectedly.
export function normalizeFilters(raw) {
  const out = { ...DEFAULT_FILTERS };
  if (raw && typeof raw === 'object') {
    Object.keys(DEFAULT_FILTERS).forEach((k) => {
      if (typeof raw[k] === 'boolean') out[k] = raw[k];
    });
  }
  return out;
}

export function loadFilters(storage) {
  try {
    const store = storage || (typeof window !== 'undefined' ? window.localStorage : null);
    if (!store) return { ...DEFAULT_FILTERS };
    const raw = store.getItem(FILTERS_STORAGE_KEY);
    return normalizeFilters(raw ? JSON.parse(raw) : null);
  } catch (_e) {
    return { ...DEFAULT_FILTERS };
  }
}

export function saveFilters(filters, storage) {
  try {
    const store = storage || (typeof window !== 'undefined' ? window.localStorage : null);
    if (!store) return;
    store.setItem(FILTERS_STORAGE_KEY, JSON.stringify(normalizeFilters(filters)));
  } catch (_e) {
    /* best-effort: persistence must never break the calendar */
  }
}

// A row passes when BOTH its source toggle AND its department toggle are ON.
export function appointmentVisible(appt, filters) {
  const f = normalizeFilters(filters);
  const src = appt._source || deriveSource(appt);
  const dept = appt._department || deriveDepartment(appt);
  return Boolean(f[src]) && Boolean(f[dept]);
}

export function filterAppointments(appts, filters) {
  return (appts || []).filter((a) => appointmentVisible(a, filters));
}

// Count of synced B.PRO rows present in the loaded list. These are native rows
// upserted by the B.PRO citas SYNC with source="bpro" (they render even when the
// LIVE B.PRO read is down), so a positive count proves synced data IS present.
export function bproSyncedCount(appointments) {
  let n = 0;
  for (const a of appointments || []) {
    const src = String((a && (a._source || a.source)) || '').trim().toLowerCase();
    if (src === SOURCE_BPRO) n += 1;
  }
  return n;
}

// "sincronizado hace X min" note for the B.PRO banner (feat bpro-citas-sync-sep4).
// The B.PRO citas SYNC upserts native rows with source="bpro" and stamps
// updated_at on every push, so the freshest B.PRO row's updated_at is an honest
// "last synced" signal WITHOUT a new endpoint. Pure: returns an i18n {key, opts}
// pair (or null when there are no synced B.PRO rows). When rows exist, opts also
// carries `count` so a caller can render "... (N citas)" from the same signal.
export function syncedAgoLabel(appointments, now = Date.now()) {
  let newest = 0;
  const count = bproSyncedCount(appointments);
  for (const a of appointments || []) {
    const src = String((a && (a._source || a.source)) || '').trim().toLowerCase();
    if (src !== SOURCE_BPRO) continue;
    const ts = Date.parse(String((a && (a.updated_at || a.created_at)) || ''));
    if (!Number.isNaN(ts) && ts > newest) newest = ts;
  }
  if (!newest) return null;
  const mins = Math.max(0, Math.round((now - newest) / 60000));
  if (mins < 1) return { key: 'calendar.bproSyncedJustNow', opts: { count } };
  if (mins < 60) return { key: 'calendar.bproSyncedMinutesAgo', opts: { minutes: mins, count } };
  const hours = Math.round(mins / 60);
  if (hours < 24) return { key: 'calendar.bproSyncedHoursAgo', opts: { hours, count } };
  return { key: 'calendar.bproSyncedDaysAgo', opts: { days: Math.round(hours / 24), count } };
}

// ---------------------------------------------------------------------------
// B.PRO sync schedule awareness (verify-bpro-sync-stale-saturday-oct10).
// The citas are PUSHED by the structDatafy cron `*/20 7-19 * * 1-6` in
// America/Mexico_City (slots 07:00..19:40 every 20 min, Mon-Sat, no Sunday).
// "Sincronizado hace 5 h" inside that window means the sync is STALLED, so the
// banner must say "sync atrasado" instead of a reassuring blue pill. Mirrors the
// backend `bpro_sync_status` slot logic. Pure + clock-injectable.
// Mexico City has had NO DST since 2022-10-30, so a fixed UTC-6 offset is exact.
// ---------------------------------------------------------------------------
export const BPRO_SYNC_SCHEDULE = Object.freeze({
  utcOffsetMin: -360,
  days: Object.freeze([1, 2, 3, 4, 5, 6]), // Date#getUTCDay: Mon..Sat (Sunday=0 off)
  startHour: 7,
  endHour: 19,
  cadenceMin: 20,
  graceMin: 10,
  missedSlotsTolerated: 2,
  label: '07:00–19:40 cada 20 min, lun–sáb',
});

function _mxShift(ts) {
  return new Date(ts + BPRO_SYNC_SCHEDULE.utcOffsetMin * 60000);
}
function _mxUnshift(utcMs) {
  return utcMs - BPRO_SYNC_SCHEDULE.utcOffsetMin * 60000;
}

// Latest scheduled slot start (epoch ms) <= ts, walking back over closed hours
// and Sundays. Null only when the schedule has no days.
export function bproSyncSlotAtOrBefore(ts) {
  const S = BPRO_SYNC_SCHEDULE;
  const lastMin = Math.floor(59 / S.cadenceMin) * S.cadenceMin;
  let d = _mxShift(ts);
  for (let i = 0; i < 10; i += 1) {
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth();
    const day = d.getUTCDate();
    if (S.days.includes(d.getUTCDay())) {
      if (d.getUTCHours() > S.endHour) {
        return _mxUnshift(Date.UTC(y, m, day, S.endHour, lastMin));
      }
      if (d.getUTCHours() >= S.startHour) {
        const mins = Math.floor(d.getUTCMinutes() / S.cadenceMin) * S.cadenceMin;
        return _mxUnshift(Date.UTC(y, m, day, d.getUTCHours(), mins));
      }
    }
    d = new Date(Date.UTC(y, m, day - 1, 23, 59));
  }
  return null;
}

// The most recent slot whose push should ALREADY have landed (slot <= now - grace).
export function bproSyncLastDueSlot(now = Date.now()) {
  return bproSyncSlotAtOrBefore(now - BPRO_SYNC_SCHEDULE.graceMin * 60000);
}

// Count due slots in (freshestTs, lastDue] that landed nothing (cap 500).
export function bproSyncMissedSlots(freshestTs, now = Date.now(), cap = 500) {
  const due = bproSyncLastDueSlot(now);
  if (due == null) return 0;
  if (!freshestTs) return cap;
  let missed = 0;
  let s = due;
  while (s != null && s > freshestTs && missed < cap) {
    missed += 1;
    s = bproSyncSlotAtOrBefore(s - 60000);
  }
  return missed;
}

// Schedule-aware verdict from the freshest synced B.PRO row. Returns null when
// there are no synced rows (nothing to judge). Pure.
export function bproSyncStatus(appointments, now = Date.now()) {
  let newest = 0;
  for (const a of appointments || []) {
    const src = String((a && (a._source || a.source)) || '').trim().toLowerCase();
    if (src !== SOURCE_BPRO) continue;
    const ts = Date.parse(String((a && (a.updated_at || a.created_at)) || ''));
    if (!Number.isNaN(ts) && ts > newest) newest = ts;
  }
  if (!newest) return null;
  const missed = bproSyncMissedSlots(newest, now);
  const lastDueAt = bproSyncLastDueSlot(now);
  return {
    overdue: missed >= BPRO_SYNC_SCHEDULE.missedSlotsTolerated,
    missed,
    lastDueAt,
    schedule: BPRO_SYNC_SCHEDULE.label,
  };
}

// Counts per source/department over the full (untagged-or-tagged) loaded list,
// so each toggle can show how many it controls.
export function computeCounts(appts) {
  const counts = {
    [SOURCE_BPRO]: 0, [SOURCE_AIVOICE]: 0,
    [DEPT_VENTAS]: 0, [DEPT_POSTVENTA]: 0,
  };
  (appts || []).forEach((a) => {
    const src = a._source || deriveSource(a);
    const dept = a._department || deriveDepartment(a);
    if (src in counts) counts[src] += 1;
    if (dept in counts) counts[dept] += 1;
  });
  return counts;
}
